import { FunctionComponent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AppLogo from "@/assets/images/favicon.png";

import { LoadingOverlay } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { ROUTE_CONFIG } from "@/routes/config";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { createChangePasswordRequestBody } from "./schema/set-password";
import {
	getSetPasswordRedirectPath,
	getSetPasswordTokenError,
	resolveLinkProblem,
} from "../SetPassword/utils/set-password-response";
import { InvitationLinkNotice } from "../SetPassword/components/InvitationLinkNotice";
import {
	PASSWORD_COMPLEXITY_REQUIRED_SCORE,
	PasswordComplexityField,
	usePasswordComplexity,
} from "../SetPassword/components/PasswordComplexityField";

import {
	useChangePasswordWithTokenMutation,
	useValidateInvitationTokenQuery,
} from "../../redux/auth.api";

/**
 * Forgotten-password page (`/change-password?token=…`, linked from the reset email). Mirrors the
 * invitation SetPassword page: same card design, the shared complexity meter + live requirement
 * hints, and the same up-front token validation with a blocking notice for dead links.
 */
export const ChangePassword: FunctionComponent = () => {
	const { t } = useTranslation();

	const [currentPassword, setCurrentPassword] = useState("");
	const [tokenStatusError, setTokenStatusError] = useState<ERROR_TYPE | null>(null);

	const passwordComplexity = usePasswordComplexity(currentPassword);

	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const token = searchParams.get("token") || "";

	const { data: tokenValidation, isLoading: isValidatingToken } = useValidateInvitationTokenQuery(
		token,
		{ skip: !token }
	);

	const [changePassword, { isLoading, error }] = useChangePasswordWithTokenMutation();

	const onSubmit = async () => {
		try {
			setTokenStatusError(null);

			const response = await changePassword(
				createChangePasswordRequestBody({ password: currentPassword }, token)
			).unwrap();

			const redirectPath = getSetPasswordRedirectPath(response);

			if (redirectPath) {
				navigate(redirectPath);

				return;
			}

			setTokenStatusError(getSetPasswordTokenError(response, "reset"));
		} catch (err) {
			console.error(err);
		}
	};

	const passwordErrors = getAllErrors(error, tokenStatusError ? [tokenStatusError] : []);

	if (token && isValidatingToken) {
		return <LoadingOverlay />;
	}

	// A missing token or any non-OPEN status (EXPIRED / USED / INVALID) is surfaced up front via the
	// notice instead of letting the user submit a password that will fail.
	const linkProblem = resolveLinkProblem(token, tokenValidation?.status);

	if (linkProblem) {
		return <InvitationLinkNotice flow="reset" status={linkProblem} token={token} />;
	}

	return (
		<div className={`w-full h-full flex flex-row items-center justify-center `}>
			<div className="flex flex-col bg-white p-6 rounded-[20px] w-[428px] max-w-[100%] shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)] items-center justify-center">
				<img alt="logo" className="w-[48px] h-[48px] mb-4" src={AppLogo} />
				<div className="text-center text-md font-bold mb-4">{t("setPassword.resetTitle")}</div>
				<div className="text-center text-ssm font-normal mb-8">
					{t("setPassword.resetSubtitle")}
				</div>
				<PasswordComplexityField
					complexity={passwordComplexity}
					label={t("setPassword.passwordRule")}
					password={currentPassword}
					placeholder={t("setPassword.resetPlaceholder")}
					onChange={setCurrentPassword}
					onEnter={onSubmit}
				/>
				{passwordErrors.length ? (
					<div className="w-full mb-4">{renderErrorMessages(passwordErrors)}</div>
				) : null}
				<div className="w-full mb-3">
					<Button
						disabled={passwordComplexity < PASSWORD_COMPLEXITY_REQUIRED_SCORE}
						isLoading={isLoading}
						size={ButtonSize.ML}
						title={t("basics.resetPassword")}
						onClick={onSubmit}
					/>
				</div>
				<button
					className="text-brand-light text-ssm font-normal cursor-pointer hover:underline"
					type="button"
					onClick={() => navigate(ROUTE_CONFIG.LOGIN)}
				>
					{t("setPassword.backToLogin")}
				</button>
			</div>
		</div>
	);
};
